import { NavLink } from 'react-router-dom';
import { Home, BarChart2, X, Settings } from 'lucide-react';
import { useIntl } from 'react-intl';
import { cn } from '@/shared/lib/utils';
import { MaintenanceHubLogoIcon } from '@/shared/ui/icons';
import { ROUTER_PATHS } from '@/app/routes';
import { useCurrentUser } from '@/features/auth/model/use-current-user';

interface NavItem {
	label: string;
	href: string;
	icon: React.ComponentType<{ className?: string }>;
}

interface SidebarProps {
	isOpen?: boolean;
	onClose?: () => void;
}

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
	const { formatMessage } = useIntl();
	const { userId, userName } = useCurrentUser();
	const initials =
		userName
			?.split(' ')
			.map((n) => n[0])
			.join('')
			.toUpperCase()
			.slice(0, 2) ?? '';

	const navItems: NavItem[] = [
		{ label: formatMessage({ id: 'sidebar.nav.home' }), href: ROUTER_PATHS.root.getLink(), icon: Home },
		{ label: formatMessage({ id: 'sidebar.nav.reports' }), href: ROUTER_PATHS.reports.getLink(), icon: BarChart2 },
		{
			label: formatMessage({ id: 'sidebar.nav.settings' }),
			href: ROUTER_PATHS.settings.getLink(),
			icon: Settings,
		},
	];

	return (
		<aside
			className={cn(
				'flex flex-col h-full w-64 bg-brand-dark text-white',
				'fixed inset-y-0 left-0 z-30 transition-transform duration-300 ease-in-out',
				'lg:static lg:translate-x-0',
				isOpen ? 'translate-x-0' : '-translate-x-full'
			)}
		>
			{/* Logo Section */}
			<div className="flex items-center gap-4 px-6 py-6 border-b border-gray-800">
				<MaintenanceHubLogoIcon className="w-7 h-6 text-brand-primary" />
				<div className="flex-1">
					<h1 className="text-lg font-bold tracking-wide">MaintenanceHub</h1>
					<p className="text-xs text-gray-400 tracking-wider">{formatMessage({ id: 'sidebar.app.tagline' })}</p>
				</div>
				{/* Close button — mobile only */}
				{onClose && (
					<button
						className="lg:hidden p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
						onClick={onClose}
						aria-label={formatMessage({ id: 'sidebar.closeMenu' })}
					>
						<X className="w-5 h-5" />
					</button>
				)}
			</div>

			{/* Navigation */}
			<nav className="flex-1 px-4 py-4 overflow-y-auto">
				<ul className="space-y-1">
					{navItems.map((item) => (
						<li key={item.href}>
							<NavLink
								to={item.href}
								end={item.href === ROUTER_PATHS.root.getLink()}
								onClick={onClose}
								className={({ isActive }) =>
									cn(
										'flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium transition-colors',
										isActive
											? 'bg-brand-primary text-white shadow-lg'
											: 'text-gray-300 hover:bg-gray-800 hover:text-white'
									)
								}
							>
								<item.icon className="w-4 h-4" />
								<span>{item.label}</span>
							</NavLink>
						</li>
					))}
				</ul>
			</nav>

			{/* User Profile */}
			<div className="px-4 py-4 border-t border-gray-800">
				<NavLink
					to={userId ? ROUTER_PATHS.userProfile.getLink(userId) : '#'}
					onClick={onClose}
					className="flex items-center gap-3 hover:opacity-80 transition-opacity"
				>
					<div className="relative">
						<div className="w-10 h-10 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center text-sm font-medium">
							{initials}
						</div>
						<div className="absolute bottom-0 right-0 w-3 h-3 bg-status-success-solid border-2 border-brand-dark rounded-full" />
					</div>
					<div>
						<p className="text-sm font-semibold">{userName}</p>
					</div>
				</NavLink>
			</div>
		</aside>
	);
}
